import { useMemo, useState } from "react";

export const PAGE_SIZES = [50, 100, 200] as const;

/**
 * Paginação em memória das listas já carregadas. A página efetiva é limitada
 * durante o render: quando um filtro encolhe a lista, o slice e o contador já
 * usam o valor correto (sem um frame vazio nem "101–10 de 10").
 */
export function usePagedList<T>(items: T[], initialSize = 50) {
  const [size, setSize] = useState(initialSize);
  const [page, setPage] = useState(1);
  const pages = Math.max(1, Math.ceil(items.length / size));
  const safePage = Math.min(page, pages);
  const pageItems = useMemo(
    () => items.slice((safePage - 1) * size, safePage * size),
    [items, safePage, size],
  );
  return {
    pageItems,
    page: safePage,
    pages,
    size,
    total: items.length,
    changeSize: (value: number) => {
      setSize(value);
      setPage(1);
    },
    prev: () => setPage(Math.max(1, safePage - 1)),
    next: () => setPage(Math.min(pages, safePage + 1)),
  };
}

type ListControlsProps = {
  page: number;
  pages: number;
  size: number;
  total: number;
  onSize: (size: number) => void;
  onPrev: () => void;
  onNext: () => void;
};

export function ListControls({
  page,
  pages,
  size,
  total,
  onSize,
  onPrev,
  onNext,
}: ListControlsProps) {
  return (
    <div className="list-controls">
      <span>
        {total === 0
          ? "Nenhum registro"
          : `${(page - 1) * size + 1}–${Math.min(page * size, total)} de ${total}`}
      </span>
      <label>
        Linhas
        <select
          value={size}
          onChange={(event) => onSize(Number(event.target.value))}
        >
          {PAGE_SIZES.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <div className="list-pager">
        <button
          className="small-button"
          disabled={page <= 1}
          onClick={onPrev}
        >
          ‹ Anterior
        </button>
        <span>
          {page} / {pages}
        </span>
        <button
          className="small-button"
          disabled={page >= pages}
          onClick={onNext}
        >
          Próxima ›
        </button>
      </div>
    </div>
  );
}
